import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, TextField, Button, Grid, Box, Divider, Container } from '@mui/material';
import { useSubscriptionInfo } from '../hooks/useSubscriptionInfo';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import locales from "../locales.json";  // Importation du fichier locales.json
import { useLang } from "../context/LangContext";
import { useNavigate } from 'react-router-dom';

const LicenseAdjustmentPage = () => {
  const { numberOfLicenses, fetchSubscriptionInfo } = useSubscriptionInfo();
  const pricePerLicense = 49.00; // Ajustez selon votre logique

  const [newLicenseCount, setNewLicenseCount] = useState(0);
  const { lang } = useLang();
  const navigate = useNavigate();
  const { payment_page, license_adjustment_page } = locales;

  // Calcul des montants
  const totalAmount = pricePerLicense * newLicenseCount;  // Montant total, TVA incluse
  const vatRate = 0.077;  // Taux de TVA suisse
  const price = totalAmount / (1 + vatRate);  // Prix avant TVA
  const vatAmount = totalAmount - price;  // TVA sur le prix

  // Formatage du montant en CHF
  const formatCurrency = (amount) => `${amount.toFixed(2)} CHF`;

  // Synchroniser l'état après le fetch des données
  useEffect(() => {
    if (numberOfLicenses > 0) {
      setNewLicenseCount(numberOfLicenses);
    }
  }, [numberOfLicenses]);

  const handleLicenseChange = (e) => {
    setNewLicenseCount(Math.max(2, parseInt(e.target.value) || 0));
  };

  const handleFinalizeOrder = async () => {
    try {
      const response = await fetchWithAuth('/subscriptions/update-licenses', {
        method: 'POST',
        body: JSON.stringify({ 
          newLicenseCount 
        }),
      });

      const data = await response.json();
      if (data.error) {
        alert(`${license_adjustment_page.error_prefix[lang]} ${data.message}`);
      } else {
        alert(license_adjustment_page.order_success[lang]);
        navigate('/account');
      }
      fetchSubscriptionInfo();

    } catch (error) {
      console.error('Erreur lors de la finalisation de la commande:', error);
      alert(license_adjustment_page.order_error[lang]);
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        minHeight: { xs: '100vh', md: 'calc(100vh - 64px)' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 1, sm: 2, md: 0 },
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Card sx={{
        width: '100%',
        maxWidth: 800,
        padding: { xs: 2, sm: 3, md: 4 },
        boxShadow: 3,
        mx: 'auto',
      }}>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ textAlign: { xs: 'center', md: 'left' } }}>
            {license_adjustment_page.title[lang]}
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6">{license_adjustment_page.current_subscription[lang]}</Typography>
              <Typography variant="body1">
                {numberOfLicenses} {license_adjustment_page.licenses[lang]} - {formatCurrency(numberOfLicenses * pricePerLicense)} / {license_adjustment_page.month[lang]}
              </Typography>

              <TextField
                type="number"
                label={license_adjustment_page.license_count_label[lang]}
                value={newLicenseCount}
                onChange={handleLicenseChange}
                fullWidth
                margin="dense"
                inputProps={{ min: 2 }}
                sx={{ marginTop: { xs: 4, md: 8 } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6">{license_adjustment_page.future_projection[lang]}</Typography>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1">{payment_page.sub_total[lang]}</Typography>
                <Typography variant="h6">{formatCurrency(price)}</Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1">{license_adjustment_page.vat_label[lang]}</Typography>
                <Typography variant="h6">{formatCurrency(vatAmount)}</Typography>
              </Box>

              <Divider sx={{ marginY: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                <Typography variant="body1">{payment_page.total[lang]}</Typography>
                <Typography variant="h6">{formatCurrency(totalAmount)}</Typography>
              </Box>

              <Typography variant="body2" color="textSecondary">
                {license_adjustment_page.final_amount_info[lang]}
              </Typography>
            </Grid>
          </Grid>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              marginTop: 2,
              fontSize: { xs: '1rem', md: '1.1rem' },
              borderRadius: '24px',
              py: { xs: 1, md: 1.5 },
            }}
            onClick={handleFinalizeOrder}
          >
            {license_adjustment_page.finalize_order_button[lang]}
          </Button>
        </CardContent>
      </Card>
    </Container>
  );
};

export default LicenseAdjustmentPage;
